import dayjs from 'dayjs';
import { HumanName, Practitioner } from '@medplum/fhirtypes';
import { MRT_ColumnDef, MRT_RowData } from 'mantine-react-table';
import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import { PeopleCell } from '../../components/PersonCell';
import { Track } from '@bariendo/lib/constants';
import { PaymentTrackCell, QUEUE_EMPTY_COLOR, TwoLineCell } from '../../components/QueueCells';
import { QUEUE_COLUMN_SIZE } from '../../components/QueueTable';
import { LastCommunicationCell, PayorCell, PnConsultationCell } from '../../services/listRowShared';
import { Group } from '@mantine/core';

/**
 * The cells every hub tab shares (2.2). Each accessor tolerates a missing row:
 * MRT hands skeleton rows (`original` is `{}`) to every callback while loading.
 */

interface HubRowLike extends MRT_RowData {
  patient?: { name?: HumanName[] };
  location?: string;
  procedure?: string;
  track?: Track;
  employer?: string;
  /** Under the Insurance pill, where a tab carries it (BAR-3096). */
  insurer?: string;
  lastCommunication?: LastCommunicationCell;
  patientNavigatorPractitioners?: Practitioner[];
}

/** Legacy columns the hub's shared cells replace. */
const REPLACED_BY_HUB = new Set(['firstName', 'lastName', 'location', 'patientNavigator']);

const LAST_CONTACT_SIZE = 76;

/** Name over market and procedure. */
export function patientColumn<Row extends HubRowLike>(): MRT_ColumnDef<Row> {
  return {
    id: 'patient',
    header: 'Patient',
    size: QUEUE_COLUMN_SIZE.patient,
    accessorFn: (row) => formatGivenFamilyName(row?.patient?.name?.[0]) ?? '',
    Cell: ({ row }) => (
      <TwoLineCell
        primary={
          <span style={{ fontWeight: 600 }}>{formatGivenFamilyName(row.original?.patient?.name?.[0]) || '—'}</span>
        }
        secondary={[row.original?.location, row.original?.procedure].filter(Boolean).join(' · ') || undefined}
      />
    ),
  };
}

/** The track pill, with the administrator and employer under an employer track. */
export function paymentTrackColumn<Row extends HubRowLike>(): MRT_ColumnDef<Row> {
  return {
    id: 'paymentTrack',
    header: 'Payment track',
    size: QUEUE_COLUMN_SIZE.paymentTrack,
    enableSorting: false,
    Cell: ({ row }) => (
      <PaymentTrackCell track={row.original?.track} employer={row.original?.employer} insurer={row.original?.insurer} />
    ),
  };
}

/** Avatars only, with photos; the name is on hover. */
export function pnColumn<Row extends HubRowLike>(): MRT_ColumnDef<Row> {
  return {
    id: 'pn',
    header: 'PN',
    size: QUEUE_COLUMN_SIZE.pn,
    enableSorting: false,
    Cell: ({ row }) => (
      <PeopleCell names={[]} practitioners={row.original?.patientNavigatorPractitioners ?? []} withNames={false} />
    ),
  };
}

interface CoverageRowLike extends MRT_RowData {
  pnConsultation?: PnConsultationCell;
  payor?: PayorCell;
}

const PN_CONSULTATION_SIZE = 120;
const PAYOR_SIZE = 140;

/** The PN's avatar and the date of the consult that started the coverage path, in one cell (BAR-3094). */
export function pnConsultationColumn<Row extends CoverageRowLike>(): MRT_ColumnDef<Row> {
  return {
    id: 'pnConsultation',
    header: 'PN consultation',
    size: PN_CONSULTATION_SIZE,
    accessorFn: (row) => row?.pnConsultation?.date ?? '',
    sortingFn: 'datetime',
    Cell: ({ row }) => {
      const consult = row.original?.pnConsultation;
      if (!consult?.date) {
        return <TwoLineCell primary={<span style={{ color: QUEUE_EMPTY_COLOR }}>—</span>} />;
      }
      return (
        <Group gap={8} wrap="nowrap">
          {consult.practitioner && <PeopleCell names={[]} practitioners={[consult.practitioner]} withNames={false} />}
          <span>{dayjs(consult.date).format('MMM D')}</span>
        </Group>
      );
    },
  };
}

/** Narrow: Administrator on the Employer tab, Insurer on the Insurance tab (BAR-3094). */
export function payorColumn<Row extends CoverageRowLike>(header: string): MRT_ColumnDef<Row> {
  return {
    id: 'payor',
    header,
    size: PAYOR_SIZE,
    accessorFn: (row) => row?.payor?.name ?? '',
    Cell: ({ row }) => {
      const payor = row.original?.payor;
      return (
        <TwoLineCell
          primary={payor?.name ?? <span style={{ color: QUEUE_EMPTY_COLOR }}>—</span>}
          secondary={payor?.detail}
        />
      );
    },
  };
}

/**
 * A date: who sent it is not this column's question. On the chasing tabs the
 * attempt count sits under it, left off at zero.
 */
export function lastContactColumn<Row extends HubRowLike>(): MRT_ColumnDef<Row> {
  return {
    id: 'lastContact',
    header: 'Last contact',
    size: LAST_CONTACT_SIZE,
    // As in the prototype, only Patient and the tab's date column sort.
    enableSorting: false,
    Cell: ({ row }) => {
      const sent = row.original?.lastCommunication?.sent;
      const attempts = row.original?.lastCommunication?.attempts;
      return (
        <TwoLineCell
          primary={sent ? dayjs(sent).format('MMM D') : <span style={{ color: QUEUE_EMPTY_COLOR }}>—</span>}
          secondary={attempts ? `${attempts} ${attempts === 1 ? 'attempt' : 'attempts'}` : undefined}
        />
      );
    },
  };
}

/**
 * A tab's columns for the hub: its leading shared cells, then its own columns
 * minus the ones those cells replace, with Last communication as Last contact.
 */
export function toHubColumns<Row extends HubRowLike>(
  leading: MRT_ColumnDef<Row>[],
  legacy: MRT_ColumnDef<Row>[]
): MRT_ColumnDef<Row>[] {
  const rest = legacy
    .filter((column) => !REPLACED_BY_HUB.has(column.id ?? ''))
    .map((column) => (column.id === 'lastCommunication' ? lastContactColumn<Row>() : column));
  return withGrowingLastColumn([...leading, ...rest]);
}

/**
 * Until a tab's rebuild brings Notes — the one column that grows — its last
 * column takes the slack, so the table still spans the card.
 */
export function withGrowingLastColumn<Row extends MRT_RowData>(columns: MRT_ColumnDef<Row>[]): MRT_ColumnDef<Row>[] {
  return columns.map((column, index) => (index === columns.length - 1 ? { ...column, grow: true } : column));
}
